import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridColDef, GridCellParams } from '@mui/x-data-grid';
import {
  DeleteForeverRounded as DeleteIcon,
  SaveRounded as SaveIcon,
  AddRounded as AddIcon,
  ArrowBackRounded as BackIcon,
  EditRounded as EditIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
  Switch
} from '@mui/material';

import { Error } from 'spot-store';
import styled from '@emotion/styled';
import {
  CenteredRow,
  ConfirmDeleteAlert,
  Container,
  FormFieldWrapper,
  FormLabel,
  FormRow,
  FormSection,
  FormSectionTitle,
  FormTextInput,
  Gap,
  Row,
  Spacer
} from 'components';
import {
  GlobalSetting,
  RetailerContext,
  useErrorNotification,
  useSpot
} from 'framework';

const GlobalSettingsContainer = styled(Box)`
  position: relative;
  height: calc(100vh - 240px);
  min-height: 500px;
  flex: 1 1 40%;
`;

const DataGridContainer = styled(Box)`
  position: relative;
  height: calc(100vh - 280px);
  flex: 1 1 40%;
`;

const SidebarContainer = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  width: 500px;
  height: 100%;
  background-color: ${p => p.theme.palette.background.paper};
  border-left: 1px solid ${p => p.theme.palette.divider};
  z-index: 1;
  overflow-y: auto;
  padding: ${p => p.theme.spacing(2)};
`;

export interface GlobalSettingsProps {
  onLoadingChanged: (loading: boolean) => unknown;
}

export function GlobalSettings({ onLoadingChanged }: GlobalSettingsProps) {
  const { t } = useTranslation();
  const { spot, query, command, loading } = useSpot();
  const [globalSetting, setGlobalSetting] =
    useState<Partial<GlobalSetting> | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [settingToDeleteId, setSettingToDeleteId] = useState<number | null>(
    null
  );
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState('');

  const { displayErrors, notification } = useErrorNotification();

  const [rows, setRows] = useState<GlobalSetting[]>([]);

  useEffect(() => {
    onLoadingChanged(loading);
  }, [loading, onLoadingChanged]);

  const refresh = useCallback(async () => {
    try {
      await query('global-configuration/settings', {}, [
        'globalConfig',
        'globalSettings'
      ]);
      setRows(spot.data.globalConfig?.globalSettings?.configurations || []);
    } catch (e) {
      displayErrors(e as Error[]);
    }
  }, [query, displayErrors, spot]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validateJson = useCallback(
    (value: string) => {
      if (!value.trim()) {
        setJsonError('');
        return true;
      }

      try {
        JSON.parse(value);
        setJsonError('');
        return true;
      } catch (e) {
        setJsonError('Invalid JSON format');
        return false;
      }
    },
    [setJsonError]
  );

  const saveGlobalSetting = useCallback(
    async (gs: Partial<GlobalSetting> | null) => {
      if (!gs) {
        return;
      }

      // Validate JSON before saving
      if (!validateJson(jsonValue)) {
        return;
      }

      try {
        let parsedValue = null;
        if (jsonValue.trim()) {
          parsedValue = JSON.parse(jsonValue);
        }

        await command(`global-configuration/settings/${gs.id ? gs.id : ''}`, {
          key: gs.key,
          value: parsedValue,
          description: gs.description,
          enabled: gs.enabled
        });
        await refresh();

        if (!gs.id) {
          setShowSidebar(false);
        }
      } catch (e) {
        displayErrors(e as Error[]);
      }
    },
    [command, displayErrors, setShowSidebar, refresh, jsonValue, validateJson]
  );

  const deleteGlobalSetting = useCallback(
    async (id: number | undefined) => {
      setSettingToDeleteId(null);
      if (!id) {
        return;
      }
      try {
        await command(
          `global-configuration/settings/${id}`,
          {},
          { method: 'DELETE' }
        );
        await refresh();
        setGlobalSetting({
          key: '',
          value: null,
          description: '',
          enabled: true
        });
        setJsonValue('');
        setShowSidebar(false);
      } catch (e) {
        displayErrors(e as Error[]);
      }
    },
    [command, displayErrors, refresh]
  );

  const validate = useCallback(
    (gs: Partial<GlobalSetting> | null) => {
      if (!gs) {
        return false;
      }

      return !!gs.key && validateJson(jsonValue);
    },
    [validateJson, jsonValue]
  );

  const valid = useMemo(() => {
    return validate(globalSetting);
  }, [validate, globalSetting]);

  const createNewGlobalSetting = useCallback(() => {
    setShowSidebar(true);
    setGlobalSetting({
      key: '',
      value: null,
      description: '',
      enabled: true
    });
    setJsonValue('');
    setJsonError('');
  }, [setShowSidebar]);

  const confirmDeleteGlobalSetting = useCallback(
    async (id: number) => {
      if (id) {
        setSettingToDeleteId(id);
      }
    },
    [setSettingToDeleteId]
  );

  const rowSelected = useCallback(
    (params: GridCellParams) => {
      const setting = params.row as GlobalSetting;
      setGlobalSetting(setting);
      setJsonValue(setting.value ? JSON.stringify(setting.value, null, 2) : '');
      setJsonError('');
      setShowSidebar(true);
    },
    [setGlobalSetting, setShowSidebar]
  );

  const onBack = useCallback(async () => {
    setShowSidebar(false);
    setGlobalSetting(null);
    setJsonValue('');
    setJsonError('');
  }, [setShowSidebar]);

  const updateGlobalSetting = useCallback(
    (field: keyof GlobalSetting, value: any) => {
      setGlobalSetting(existing => ({
        ...existing,
        [field]: value
      }));
    },
    [setGlobalSetting]
  );

  const formatValue = useCallback((value: any) => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'key',
      headerName: t('key'),
      flex: 2,
      minWidth: 200
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 3,
      minWidth: 250
    },
    {
      field: 'value',
      headerName: t('value'),
      flex: 2,
      minWidth: 200,
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}
        >
          {formatValue(params.value)}
        </Typography>
      )
    },
    {
      field: 'enabled',
      headerName: t('enabled'),
      width: 100,
      renderCell: params => (
        <Switch checked={params.value} disabled size="small" />
      )
    },
    {
      field: 'updatedAt',
      headerName: t('updated'),
      width: 180,
      renderCell: params => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 120,
      sortable: false,
      renderCell: params => (
        <Box>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              rowSelected(params);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              confirmDeleteGlobalSetting(params.row.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container>
      <Row>
        <Typography variant="h6">{t('globalSettings')}</Typography>
        <Spacer />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={createNewGlobalSetting}
        >
          {t('addSetting')}
        </Button>
      </Row>
      <Gap size={1} />

      <GlobalSettingsContainer>
        <DataGridContainer>
          <DataGrid
            rows={rows}
            columns={columns}
            disableColumnMenu
            disableColumnSelector
            loading={loading}
            autoPageSize
            disableSelectionOnClick
            onCellClick={rowSelected}
          />
        </DataGridContainer>

        {showSidebar && (
          <SidebarContainer>
            <FormSection>
              <CenteredRow>
                <IconButton onClick={onBack}>
                  <BackIcon />
                </IconButton>
                <FormSectionTitle>
                  {globalSetting?.id ? t('editSetting') : t('newSetting')}
                </FormSectionTitle>
                <Spacer />
                <IconButton
                  onClick={() => saveGlobalSetting(globalSetting)}
                  disabled={!valid}
                >
                  <SaveIcon />
                </IconButton>
              </CenteredRow>

              <Gap size={2} />

              <FormRow>
                <FormFieldWrapper>
                  <FormLabel size="narrow">{t('key')}</FormLabel>
                  <FormTextInput
                    size="small"
                    value={globalSetting?.key || ''}
                    onChange={value => updateGlobalSetting('key', value)}
                    placeholder={t('enterSettingKey')}
                  />
                </FormFieldWrapper>
              </FormRow>

              <FormRow>
                <FormFieldWrapper>
                  <FormLabel size="narrow">{t('description')}</FormLabel>
                  <FormTextInput
                    size="small"
                    value={globalSetting?.description || ''}
                    onChange={value =>
                      updateGlobalSetting('description', value)
                    }
                    placeholder={t('enterSettingDescription')}
                  />
                </FormFieldWrapper>
              </FormRow>

              <FormRow>
                <FormFieldWrapper>
                  <FormLabel size="narrow">{t('valueJson')}</FormLabel>
                  <Container>
                    <FormTextInput
                      multiline
                      rows={8}
                      size="small"
                      value={jsonValue}
                      onChange={value => {
                        setJsonValue(value);
                        validateJson(value);
                      }}
                      placeholder={t('enterJsonValuePlaceholder')}
                      validate={value => validateJson(value || '')}
                    />
                    {jsonError && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ mt: 0.5, fontSize: '0.75rem' }}
                      >
                        {jsonError}
                      </Typography>
                    )}
                    {!jsonError && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontSize: '0.75rem' }}
                      >
                        {t('enterValidJsonValue')}
                      </Typography>
                    )}
                  </Container>
                </FormFieldWrapper>
              </FormRow>

              <FormRow>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={globalSetting?.enabled || false}
                      onChange={e =>
                        updateGlobalSetting('enabled', e.target.checked)
                      }
                    />
                  }
                  label={t('enabled')}
                />
              </FormRow>

              {globalSetting?.id && (
                <>
                  <Gap size={2} />
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() =>
                      confirmDeleteGlobalSetting(globalSetting.id!)
                    }
                    fullWidth
                  >
                    {t('deleteSetting')}
                  </Button>
                </>
              )}
            </FormSection>
          </SidebarContainer>
        )}
      </GlobalSettingsContainer>

      <ConfirmDeleteAlert
        id="delete-global-setting"
        value={settingToDeleteId}
        title={t('deleteGlobalSetting')}
        text={t('deleteGlobalSettingConfirmation')}
        open={!!settingToDeleteId}
        onConfirm={id => {
          if (id) {
            deleteGlobalSetting(id);
          } else {
            setSettingToDeleteId(null);
          }
        }}
        keepMounted={false}
      />

      {notification}
    </Container>
  );
}
